import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../components/AppIcon';

const API_URL = 'https://api.autocasses.com/api/produit/all';

// Couleurs modernes
const COLORS = {
  primary: '#3b82f6',
  secondary: '#3b82f6',
  accent: '#7209b7',
  background: '#f8f9fa',
  card: '#ffffff',
  text: '#212529',
  textLight: '#6c757d',
  border: '#dee2e6',
  success: '#4cc9f0',
  warning: '#f72585',
};

// Composant de carte produit amélioré pour web
const ProductCard = React.memo(({ product, onPress, onSellerPress }) => {
  const [sellerAvatar, setSellerAvatar] = useState(null);
  const [userToken, setUserToken] = useState(null);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('userToken');
        setUserToken(token);
        
        if (token && product.user?.id) {
          await fetchSellerAvatar(token);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, [product.user]);

  const fetchSellerAvatar = async (token) => {
    if (!product.user?.id) return;
    
    try {
      const response = await axios.get(
        `https://api.autocasses.com/api/user/get/${product.user.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          timeout: 10000,
        }
      );
      
      if (response.data.avatar) {
        setSellerAvatar(response.data.avatar);
      }
    } catch (error) {
      console.error('Error fetching seller avatar:', error);
    }
  };

  // Formatter le prix
  const formatPrice = (price) => {
    return parseInt(price).toLocaleString('fr-FR') + ' FCFA';
  };

  // Obtenir la première photo
  const getProductImage = () => {
    if (product.photos && product.photos.length > 0) {
      return product.photos[0].photo_url;
    }
    return 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&auto=format&fit=crop';
  };

  // Obtenir l'avatar du vendeur
  const getSellerAvatar = () => {
    if (sellerAvatar) {
      if (sellerAvatar.startsWith('http')) {
        return sellerAvatar;
      } else {
        return `https://api.autocasses.com/uploads/avatars/${sellerAvatar}`;
      }
    }
    
    // Avatar par défaut basé sur le nom
    const sellerName = product.user?.nom || 'Vendeur';
    const initials = sellerName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
    
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    const colorIndex = sellerName.charCodeAt(0) % colors.length;
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${colors[colorIndex].replace('#', '')}&color=fff&size=128`;
  };

  // Obtenir le libellé de l'état
  const getStateLabel = (etat) => {
    if (!etat) return 'État';
    
    const etatLower = etat.toLowerCase();
    if (etatLower.includes('neuf') || etat === 'new') {
      return 'Neuf';
    } else if (etatLower.includes('comme neuf') || etatLower.includes('like')) {
      return 'Comme neuf';
    } else {
      return 'Occasion';
    }
  };

  // Obtenir le type de vendeur
  const getSellerType = (user) => {
    if (user.type_pro === 'garage') {
      return 'Garage';
    } else if (user.type_pro === 'mecanicien') {
      return 'Mécanicien';
    } else if (user.role === 'vendeur') {
      return 'Professionnel';
    } else {
      return 'Particulier';
    }
  };

  // Gérer le clic sur le vendeur
  const handleSellerPress = (e) => {
    e.stopPropagation();
    if (product.user?.id) {
      onSellerPress(product.user.id, product.user.nom || 'Vendeur');
    }
  };

  return (
    <div 
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      onClick={() => onPress(product)}
    >
      {/* Badge État */}
      <div className="absolute top-4 left-4 z-10">
        <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold ${
          product.etat === 'Neuf' || product.etat === 'Comme neuf' 
            ? 'bg-emerald-500 text-white' 
            : 'bg-blue-500 text-white'
        }`}>
          {getStateLabel(product.etat)}
        </div>
      </div>

      {/* Badge Enchère */}
      {product.enchere === 1 && (
        <div className="absolute top-4 right-4 z-10 inline-flex items-center bg-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold">
          <Icon name="Gavel" size={12} color="white" className="mr-1.5" />
          <span>Enchère</span>
        </div>
      )}

      {/* Image du produit */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={getProductImage()}
          alt={product.titre}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&auto=format&fit=crop';
          }}
        />
        
        {/* Overlay dégradé */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/30 to-transparent" />
        
        {/* Informations sur l'image */}
        <div className="absolute bottom-4 left-4 flex gap-2 z-10">
          {product.negociable === "true" && (
            <div className="flex items-center bg-black/70 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
              <Icon name="Handshake" size={12} color="white" className="mr-1.5" />
              <span>Négociable</span>
            </div>
          )}
          {product.echange === "true" && (
            <div className="flex items-center bg-purple-600/80 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
              <Icon name="RefreshCw" size={12} color="white" className="mr-1.5" />
              <span>Échange</span>
            </div>
          )}
        </div>
      </div>

      {/* Contenu de la carte */}
      <div className="p-6">
        {/* Titre */}
        <h3 className="font-bold text-lg text-slate-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {product.titre}
        </h3>

        {/* Prix */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-2xl font-bold text-blue-700">{formatPrice(product.prix)}</p>
            <div className="flex items-center text-slate-600 mt-1">
              <Icon name="MapPin" size={14} color="#64748b" className="mr-2" />
              <span className="text-sm">{product.city || product.region || 'Localisation inconnue'}</span>
            </div>
          </div>
          {product.garantie && (
            <div className="flex items-center bg-emerald-50 text-emerald-800 px-3 py-1.5 rounded-lg text-sm font-semibold">
              <Icon name="Shield" size={14} color="#10b981" className="mr-1.5" />
              <span>{product.garantie}</span>
            </div>
          )}
        </div>

        {/* Date de publication */}
        <div className="flex items-center text-slate-500 mb-4">
          <Icon name="Clock" size={14} color="#64748b" className="mr-2" />
          <span className="text-sm">
            {new Date(product.createdAt).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>

        {/* Vendeur */}
        {product.user && (
          <div 
            className="flex items-center p-3 bg-slate-50 rounded-xl mb-4 cursor-pointer hover:bg-slate-100 transition-colors"
            onClick={handleSellerPress}
          >
            <img
              src={getSellerAvatar()}
              alt={product.user.nom || 'Vendeur'}
              className="w-10 h-10 rounded-full border-2 border-white shadow"
              onError={(e) => {
                const initials = (product.user.nom || 'VD').split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
                e.target.src = `https://ui-avatars.com/api/?name=${initials}&background=3b82f6&color=fff&size=128`;
              }}
            />
            <div className="ml-3 flex-1 min-w-0">
              <div className="flex items-center">
                <span className="font-semibold text-slate-900 truncate">
                  {product.user.nom || 'Vendeur'}
                </span>
                {product.user.isVerified && (
                  <Icon name="CheckCircle" size={14} color="#10b981" className="ml-2 flex-shrink-0" />
                )}
                {product.user.isPro && (
                  <span className="ml-2 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded font-bold">
                    PRO
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 truncate">
                {getSellerType(product.user)}
              </p>
            </div>
            <Icon name="ChevronRight" size={16} color="#64748b" />
          </div>
        )}

        {/* Bouton d'action */}
        <button
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center group/btn"
          onClick={(e) => {
            e.stopPropagation();
            onPress(product);
          }}
        >
          <span className="mr-2">Voir détails</span>
          <Icon name="ArrowRight" size={18} color="white" className="group-hover/btn:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
});

// Composant principal Shop adapté pour web
const Shop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [filterCondition, setFilterCondition] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const containerRef = useRef(null);

  // Vérifier si on vient d'une autre page
  const showBackButton = location.state?.from !== undefined;

  useEffect(() => {
    loadUserToken();
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, searchQuery, sortBy, filterCondition, priceRange]);

  const loadUserToken = async () => {
    try {
      const token = localStorage.getItem('userToken');
      setUserToken(token);
    } catch (error) {
      console.error('Error loading user token:', error);
    }
  };

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (userToken) {
      headers['Authorization'] = `Bearer ${userToken}`;
    }
    
    return headers;
  };

  // Charger les produits
  const fetchProducts = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(API_URL, {
        headers: getAuthHeaders(),
        timeout: 10000,
      });
      
      if (response.data.success) {
        const sortedProducts = [...response.data.data].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        
        setProducts(sortedProducts);
      } else {
        setError('Erreur lors du chargement des produits');
      }
    } catch (err) {
      console.error('Erreur API:', err);
      if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setError('Erreur de connexion. Vérifiez votre réseau.');
      }
    } finally {
      setLoading(false);
    }
  }, [userToken]);

  // Appliquer les filtres et le tri
  const applyFilters = () => {
    let filtered = [...products];
    
    // Filtrer par recherche
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => {
        return (
          product.titre?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.city?.toLowerCase().includes(query) ||
          product.etat?.toLowerCase().includes(query) ||
          product.user?.nom?.toLowerCase().includes(query)
        );
      });
    }
    
    // Filtrer par condition
    if (filterCondition !== 'all') {
      filtered = filtered.filter(product => {
        const etatLower = product.etat?.toLowerCase() || '';
        switch(filterCondition) {
          case 'new':
            return etatLower.includes('neuf') || etatLower.includes('new');
          case 'used':
            return etatLower.includes('occasion') || etatLower.includes('used');
          case 'like_new':
            return etatLower.includes('comme neuf') || etatLower.includes('like new');
          default:
            return true;
        }
      });
    }
    
    // Filtrer par prix
    filtered = filtered.filter(product => {
      const price = parseInt(product.prix) || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Trier
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'recent':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'price-low':
          return (parseInt(a.prix) || 0) - (parseInt(b.prix) || 0);
        case 'price-high':
          return (parseInt(b.prix) || 0) - (parseInt(a.prix) || 0);
        case 'auction':
          return (b.enchere || 0) - (a.enchere || 0);
        default:
          return 0;
      }
    });
    
    setFilteredProducts(filtered);
  };

  // Navigation vers les détails du produit
  const handleProductPress = (product) => {
     navigate('/product', { 
      state: { 
        productId: product.id,
        productData: product
      }
    });
  };

  // Navigation vers le profil du vendeur
  const handleSellerPress = (userId, userName) => {
    navigate(`/user/${userId}`, { state: { userName } });
  };

  // Gérer le retour
  const handleGoBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-6"></div>
          <p className="text-xl text-slate-700 font-medium">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
          <div className="bg-red-50 p-6 rounded-full inline-block mb-6">
            <Icon name="AlertTriangle" size={48} color="#ef4444" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Oups !</h2>
          <p className="text-slate-600 mb-8">{error}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={fetchProducts}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Icon name="RefreshCw" size={18} color="white" className="inline mr-2" />
              Réessayer
            </button>
            {error.includes('Session expirée') && (
              <button
                onClick={() => navigate('/auth')}
                className="flex-1 border-2 border-blue-500 text-blue-500 hover:border-blue-600 hover:text-blue-600 px-6 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                Se connecter
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header avec bouton retour */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {showBackButton && (
                <button
                  onClick={handleGoBack}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} color="white" />
                </button>
              )}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white">Boutique</h1>
                <p className="text-blue-100 mt-2">
                  {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} disponible{filteredProducts.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/create-listing')}
              className="hidden md:inline-flex items-center bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Icon name="Plus" size={18} color="white" className="mr-2" />
              <span>Vendre un produit</span>
            </button>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-8">
          {/* Barre de recherche */}
          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Rechercher un produit, une marque, un modèle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-300 rounded-xl px-6 py-3.5 pl-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700 placeholder-slate-500"
            />
            <Icon 
              name="Search" 
              size={20} 
              color="#64748b" 
              className="absolute left-4 top-1/2 transform -translate-y-1/2" 
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2"
              >
                <Icon name="X" size={20} color="#64748b" />
              </button>
            )}
          </div>

          {/* Filtres rapides */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="font-semibold text-slate-700">Filtrer par :</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterCondition('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filterCondition === 'all'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Tous
              </button>
              <button
                onClick={() => setFilterCondition('new')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filterCondition === 'new'
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Neuf
              </button>
              <button
                onClick={() => setFilterCondition('like_new')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filterCondition === 'like_new'
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Comme neuf
              </button>
              <button
                onClick={() => setFilterCondition('used')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filterCondition === 'used'
                    ? 'bg-slate-700 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Occasion
              </button>
             
            </div>
          </div>

          {/* Tri et gamme de prix */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700">Trier par :</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700 font-medium"
                >
                  <option value="recent">Plus récents</option>
                  <option value="price-low">Prix croissant</option>
                  <option value="price-high">Prix décroissant</option>
                </select>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700">Prix :</span>
                <div className="flex-1 flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="10000000"
                    step="100000"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                    className="flex-1"
                  />
                  <div className="text-sm font-medium text-slate-700 whitespace-nowrap">
                    {priceRange[0].toLocaleString()} - {priceRange[1].toLocaleString()} FCFA
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                <Icon name="Package" size={20} color="#3b82f6" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">Total produits</p>
                <p className="text-2xl font-bold text-slate-900">{products.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center">
              <div className="bg-emerald-100 p-2 rounded-lg mr-3">
                <Icon name="Star" size={20} color="#10b981" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">Produits neufs</p>
                <p className="text-2xl font-bold text-slate-900">
                  {products.filter(p => p.etat?.toLowerCase().includes('neuf')).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center">
              <div className="bg-amber-100 p-2 rounded-lg mr-3">
                <Icon name="Gavel" size={20} color="#f59e0b" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">Enchères</p>
                <p className="text-2xl font-bold text-slate-900">
                  {products.filter(p => p.enchere === 1).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center">
              <div className="bg-purple-100 p-2 rounded-lg mr-3">
                <Icon name="Users" size={20} color="#8b5cf6" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">Vendeurs pro</p>
                <p className="text-2xl font-bold text-slate-900">
                  {[...new Set(products.filter(p => p.user?.isPro).map(p => p.user?.id))].length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des produits */}
        {filteredProducts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-16 text-center">
            <div className="bg-slate-50 p-8 rounded-full inline-block mb-8">
              <Icon name="Search" size={80} color="#cbd5e1" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Aucun produit trouvé
            </h2>
            <p className="text-slate-600 text-center mb-8 max-w-md mx-auto">
              {searchQuery 
                ? `Aucun produit ne correspond à "${searchQuery}". Essayez avec d'autres termes.`
                : 'Aucun produit ne correspond à vos critères de filtrage.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterCondition('all');
                  setPriceRange([0, 10000000]);
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Icon name="Filter" size={18} color="white" className="inline mr-2" />
                Réinitialiser les filtres
              </button>
              <button
                onClick={fetchProducts}
                className="border-2 border-blue-500 text-blue-500 hover:border-blue-600 hover:text-blue-600 px-8 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                <Icon name="RefreshCw" size={18} color="currentColor" className="inline mr-2" />
                Actualiser
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Compteur de résultats */}
            <div className="mb-6 flex items-center justify-between">
              <p className="text-slate-700">
                <span className="font-bold text-blue-600">{filteredProducts.length}</span> 
                <span className="mx-2">produit{filteredProducts.length !== 1 ? 's' : ''} trouvé{filteredProducts.length !== 1 ? 's' : ''}</span>
              </p>
              <button
                onClick={fetchProducts}
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                <Icon name="RefreshCw" size={18} color="currentColor" className="mr-2" />
                Actualiser
              </button>
            </div>

            {/* Grille des produits - 4 par ligne sur desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onPress={handleProductPress}
                  onSellerPress={handleSellerPress}
                />
              ))}
            </div>
          </div>
        )}

        {/* Bouton retour en haut (flottant) */}
        {filteredProducts.length > 8 && (
          <button 
            className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <Icon name="ChevronUp" size={20} color="white" />
          </button>
        )}
      </div>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .grid > div {
          animation: fadeInUp 0.3s ease-out forwards;
          opacity: 0;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .grid > div:nth-child(n) {
          animation-delay: calc(var(--i, 0) * 0.05s);
        }
        
        input[type="range"] {
          -webkit-appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(to right, #e2e8f0, #3b82f6);
          outline: none;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default Shop;